import IconTrashBin from "assets/img/icon-trash-bin.svg?react"
import IconPencil from "assets/img/icon-pencil.svg?react"
import IconTextFormatted from "assets/img/icon-text-formatted.svg?react"

import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import cx from "classnames"

import Button from "components/Button/Button"
import { IApiLink } from "@/types"
import { Grid, GridItem } from "components/Grid"
import TextBuildingBlockModel, { ICreateTextBuildingBlockDto } from "models/TextBuildingBlock"
import Checkbox from "components/Checkbox/Checkbox"
import toastMessages from "stores/toastMessages"
import ConfirmModal from "components/ConfirmModal/ConfirmModal"
import TextBuildingBlockModal from "./TextBuildingBlockModal"
import FormattedText from "components/FormattedText/FormattedText"

export enum TextBuildingBlockMode {
    manage = "manage",
    select = "select",
}

interface ITextBuildingBlockProps {
    mode: TextBuildingBlockMode
    block: TextBuildingBlockModel
    selected?: boolean
    onUpdate?(updateLink: IApiLink, createTextBuildingBlockDto: ICreateTextBuildingBlockDto): Promise<void>
    onDelete?(deleteLink: IApiLink): Promise<void>
    onSelectionToggle?(textBuildingBlock: TextBuildingBlockModel): void
}

const TextBuildingBlock: FC<React.PropsWithChildren<ITextBuildingBlockProps>> = ({
    mode,
    block,
    selected,
    onUpdate,
    onDelete,
    onSelectionToggle,
}) => {
    const { t } = useTranslation()
    const [showEditModal, setShowEditModal] = useState(false)
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    async function handleDelete() {
        if (!block?.linkDelete || !onDelete) {
            toastMessages.showSystemError()
            return
        }
        setIsDeleting(true)
        try {
            await onDelete(block.linkDelete)
        } catch (e) {
            toastMessages.showSystemError()
        }
        setIsDeleting(false)
    }

    return (
        <Grid columns={mode === TextBuildingBlockMode.select ? '3.2rem 10% 3.2rem minmax(0, auto)' : '10% 3.2rem minmax(0, auto) max-content'}
            gap="2.4rem"
            alignItems="flex-start"
            onClick={mode === TextBuildingBlockMode.select && onSelectionToggle && block ? () => onSelectionToggle(block) : undefined}
            className={cx([
                "border-grey-100-between py-2",
                { "bg-grey-50": selected },
            ])}>
            {mode === TextBuildingBlockMode.select &&
                <GridItem justifySelf="end">
                    <Checkbox
                        checked={selected}
                        onChange={() => onSelectionToggle && block && onSelectionToggle(block)}
                    />
                </GridItem>
            }
            <GridItem className="word-break-break-word">{block.textBlockId}</GridItem>
            <GridItem>
                {block.textFormatting &&
                    <IconTextFormatted className="d-block" />
                }
            </GridItem>
            <GridItem className="word-break-break-word white-space-pre-wrap pr-3 overflow-auto">
                {block.textFormatting
                    ? <FormattedText text={block.text} />
                    : block.text
                }
            </GridItem>
            {mode === TextBuildingBlockMode.manage &&
                <>
                    <Grid columns="max-content max-content" gap="1.6rem"
                        justifyContent="flex-end">
                        {!!onDelete && !!block?.linkDelete &&
                            <Button
                                round
                                theme="grey"
                                icon={<IconTrashBin />}
                                onClick={() => setShowDeleteConfirmModal(true)}
                            />
                        }
                        {!!onUpdate && !!block?.linkUpdate &&
                            <Button
                                round
                                theme="grey"
                                icon={<IconPencil />}
                                onClick={() => setShowEditModal(true)}
                            />
                        }
                    </Grid>
                </>
            }
            {showEditModal &&
                <TextBuildingBlockModal
                    textBuildingBlock={block}
                    onUpdate={onUpdate}
                    onClose={() => setShowEditModal(false)}
                />
            }
            {showDeleteConfirmModal &&
                <ConfirmModal
                    title={t("common.warning")}
                    maxWidth="586px"
                    confirmButtonLabel={t("common.delete")}
                    theme="danger"
                    onConfirm={handleDelete}
                    onClose={() => setShowDeleteConfirmModal(false)}
                    isBusy={isDeleting}>
                    {t("textBuildingBlocks.deleteConfirm")}
                </ConfirmModal>
            }
        </Grid>
    )

}

export default TextBuildingBlock
